import { Stats } from '@/types';

/**
 * Derived pipeline numbers — all stage counts computed in one place so the
 * dashboard pipeline, stats cards, analytics tab, and admin analytics page
 * always agree.
 *
 * Every incoming event ends up in exactly ONE end-state:
 *   Filtered | Irrelevant | Relevant (uncovered) | Covered
 *
 * The "Relevant (uncovered)" bucket are events that scored above the
 * relevance threshold but haven't had a coverage document generated yet
 * (or the relevant count exceeds coverage docs).
 */
export interface PipelineNumbers {
  /** Total collected (non-expired) events */
  collected: number;
  /** Events collected in the last 10 minutes */
  recent: number;
  /** Events awaiting processing (pending + deciding) */
  pending: number;

  /** Events removed by pre-filter (LLM pre-screen + rule filter) */
  filtered: number;
  /** Events that passed through pre-filter (or were skipped/none) */
  passedPreFilter: number;

  /** Events actually scored by the full LLM */
  llmDecided: number;
  /** Average relevance score among LLM-decided events (0–1) */
  avgLLMScore: number;
  /** Average score as a display-ready percentage (0–100, rounded) */
  avgLLMScorePercent: number;

  /** Events the LLM flagged as relevant (score >= threshold) */
  relevant: number;
  /** Events the LLM scored below the relevance threshold */
  irrelevant: number;

  /** Coverage documents generated */
  covered: number;
  /** Relevant events not yet covered */
  relevantUncovered: number;

  // Pre-filter breakdown (for detail panel)
  preScreenFiltered: number;
  preScreenPassed: number;
  preScreenSkipped: number;
  ruleFiltered: number;
}

/**
 * Derive all pipeline numbers from the Stats API response.
 * This is the SINGLE source of truth for pipeline counts across the UI.
 */
export function derivePipelineNumbers(stats: Stats | null): PipelineNumbers {
  if (!stats) {
    return {
      collected: 0,
      recent: 0,
      pending: 0,
      filtered: 0,
      passedPreFilter: 0,
      llmDecided: 0,
      avgLLMScore: 0,
      avgLLMScorePercent: 0,
      relevant: 0,
      irrelevant: 0,
      covered: 0,
      relevantUncovered: 0,
      preScreenFiltered: 0,
      preScreenPassed: 0,
      preScreenSkipped: 0,
      ruleFiltered: 0,
    };
  }

  const collected = stats.total_events || 0;
  const recent = stats.recent_events || 0;
  const pending = stats.pending_count || 0;

  const preScreenFiltered = stats.pre_screen_filtered || 0;
  const preScreenPassed = stats.pre_screen_passed || 0;
  const preScreenSkipped = stats.pre_screen_skipped || 0;
  const ruleFiltered = stats.rule_filtered || 0;

  const filtered = stats.filtered_count || 0;               // preScreenFiltered + ruleFiltered
  const passedPreFilter = preScreenPassed + preScreenSkipped; // events that made it through
  const llmDecided = stats.llm_decided_count || 0;           // totalDecisions - filtered

  const avgLLMScore = stats.avg_llm_score || 0;
  const avgLLMScorePercent = Math.round(avgLLMScore * 100);

  const relevant = stats.relevant_count || 0;
  const irrelevant = Math.max(0, llmDecided - relevant);

  const covered = stats.coverage_count || 0;
  const relevantUncovered = Math.max(0, relevant - covered);

  return {
    collected,
    recent,
    pending,
    filtered,
    passedPreFilter,
    llmDecided,
    avgLLMScore,
    avgLLMScorePercent,
    relevant,
    irrelevant,
    covered,
    relevantUncovered,
    preScreenFiltered,
    preScreenPassed,
    preScreenSkipped,
    ruleFiltered,
  };
}
